import React, {useEffect, useMemo, useState} from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import {Helmet} from 'react-helmet';
import Wrapper from '../../components/Wrapper';
import {
  useCreateEquipmentMutation,
  useLazyGetCurrentEquipmentQuery,
  useLazyGetEquipmentTypeQuery,
  useLazyGetFacilityQuery,
  useUpdateEquipmentMutation,
} from '../../store/builder/inspections.api';
import {useDispatch} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import Input from '../../components/Input';
import FacilitySelect from '../../components/EquipmentComponents/FacilitySelect';
import TextField from '../../components/EquipmentComponents/TextField';
import RadioField from '../../components/EquipmentComponents/RadioField';
import DateField from '../../components/EquipmentComponents/DateField';
import SelectField from '../../components/EquipmentComponents/SelectField';
import FileField from '../../components/EquipmentComponents/FileField';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useErrorHandler} from '../../global/ErrorsHandler';
import HoseElement from '../../components/HoseElement';
import {generateUUID} from '../../global/utils';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';

const ToolsCreate = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation()?.state?.equipment ?? params?.id;
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [title, setTitle] = useState('');
  const [currentFacility, setCurrentFacility] = useState(null);
  const [hasHose, setHasHose] = useState(false);
  const [hoseFields, setHoseFields] = useState([]);
  const [resHoseFields, setResHoseFields] = useState([]);

  const [
    getFacility,
    {data: facilityData, isFetching: facilityIsLoading, error: facilityError},
  ] = useLazyGetFacilityQuery();

  const [
    getEquipment,
    {
      data: getEquipmentData,
      error: getEquipmentError,
      isError: getEquipmentIsError,
      isLoading: getEquipmentIsLoading,
      isSuccess: getEquipmentIsSuccess,
    },
  ] = useLazyGetCurrentEquipmentQuery();

  const [
    getEquipmentTypes,
    {
      data: equipmentTypesData,
      isLoading: equipmentTypesIsLoading,
      isError: equipmentTypesIsError,
      error: equipmentTypesError,
      isSuccess: equipmentTypesIsSuccess,
    },
  ] = useLazyGetEquipmentTypeQuery();

  const [
    createEquipment,
    {
      isFetching: createIsLoading,
      error: createError,
      isError: createIsError,
      isSuccess: createIsSuccess,
    },
  ] = useCreateEquipmentMutation();
  const [
    updateEquipment,
    {
      isFetching: updateIsLoading,
      error: updateError,
      isError: updateIsError,
      isSuccess: updateIsSuccess,
    },
  ] = useUpdateEquipmentMutation();

  useLoader([
    getEquipmentIsLoading,
    equipmentTypesIsLoading,
    createIsLoading,
    updateIsLoading,
  ]);

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateError,
    createError,
  ]);

  useEffect(() => {
    getFacility();
    if (params?.id) {
      getEquipment(params.id);
      setHasHose(true);
    } else {
      getEquipmentTypes();
      setHasHose(false);
    }
  }, []);

  useEffect(() => {
    if (params?.id) {
      if (getEquipmentData?.data?.fields?.length > 0) {
        setFields(
          getEquipmentData?.data?.fields?.filter(
            field => field.key !== 'last_annual_date',
          ),
        );
        setTitle(getEquipmentData?.data?.title);
        setCurrentFacility({
          value: getEquipmentData?.data?.facility?.id,
          label: getEquipmentData?.data?.facility?.name,
        });
      }
      setResHoseFields(
        getEquipmentData?.data?.hoses?.map(el => ({
          ...el,
          edit_status: 'no-edit',
        })),
      );
    } else {
      if (equipmentTypesData?.length > 0) {
        setFields(
          equipmentTypesData
            ?.filter(el => el.id === 44)
            ?.filter(field => field.key !== 'last_annual_date')[0]?.fields,
        );
      }
    }
  }, [equipmentTypesData, getEquipmentData]);

  const submitHandler = () => {
    let s = [];
    let data = {
      id: location?.id ? location?.id : location,
      title: title !== '' ? title : null,
      facility_id: currentFacility?.value ? currentFacility?.value : null,
      equipment_type_id: 44,
      fields: fields,
    };

    for (let key in data) {
      if (!data[key] && key === data.facility_id) {
        toast?.error(t('toolsCreate.messages.pleaseEnter') + ' ' + key);
        s.push(false);
      }
    }

    if (s.length === 0) {
      dispatch(setLoader(true));
      if (!location) {
        createEquipment(data)
          .then(res => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            dispatch(setLoader(false));
            toast.success(res.data.message);
            setFields([]);
            setTimeout(() => {
              navigate(-1);
            }, 500);
          })
          .catch(err => {});
      } else {
        updateEquipment({...data})
          ?.then(res => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            dispatch(setLoader(false));
            toast.success(res.data.message);
            setTimeout(() => {
              navigate(-1);
            }, 500);
          })
          .catch(err => toast.error(err?.response?.data?.message));
      }
    }
  };

  const addNewHose = () => {
    setHoseFields(old => [
      ...old,
      {
        name: '',
        cert_image: null,
        manufacture_date: null,
        edit_status: 'new',
        id: generateUUID(),
      },
    ]);
  };

  const renderCreatedHoses = useMemo(() => {
    return resHoseFields?.map((hose, index) => (
      <HoseElement
        key={index}
        hose={hose}
        index={index}
        setHoseFields={setResHoseFields}
        getEquipment={() => getEquipment(params.id)}
      />
    ));
  }, [resHoseFields]);

  return (
    <Wrapper>
      <DashboardHeader
        type={'tools-create'}
        title={params?.id ? t('toolsCreate.title.edit') : t('toolsCreate.title.new')}
      />
      <Helmet>
        <title>{params?.id ? t('toolsCreate.pageTitle.edit') : t('toolsCreate.pageTitle.new')}</title>
      </Helmet>
      <div className='equipment new' style={{padding: 10}}>
        <div className='equipmentCreate'>
          <Input
            placeholder={t('toolsCreate.form.idPlaceholder')}
            maxwidth={500}
            label={t('toolsCreate.form.idLabel')}
            value={title}
            onInput={e => {
              setTitle(() => e.target.value);
            }}
          />
          {/*{!params?.id &&  (*/}
          {/*  <FacilitySelect*/}
          {/*    currentFacility={currentFacility}*/}
          {/*    setCurrentFacility={setCurrentFacility}*/}
          {/*    facilityData={facilityData?.filter(el => el.can_edit === true)}*/}
          {/*    // setCurrentStations={setCurrentStations}*/}
          {/*    // setStationsOptions={setStationsOptions}*/}
          {/*    // setTerminalsOptions={setTerminalsOptions}*/}
          {/*  />*/}
          {/*)}*/}
          {fields &&
            fields
              ?.filter(field => field.key !== 'last_annual_date')
              ?.map(field => {
                if (field?.type === 'text' || field?.type === 'number') {
                  return (
                    <TextField
                      key={field?.key}
                      id={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      type={field?.type}
                      fields={fields}
                      value={field?.value ? field?.value : ''}
                    />
                  );
                }
                if (field?.type === 'radio') {
                  return (
                    <RadioField
                      id={field?.key}
                      key={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      options={field?.options}
                      field={field}
                    />
                  );
                }
                if (field?.type === 'date') {
                  return (
                    <DateField
                      key={field?.key}
                      id={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      field={field}
                      tooltip={field?.tooltip}
                    />
                  );
                }
                if (field?.type === 'select') {
                  return (
                    <SelectField
                      key={field?.key}
                      id={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      field={field}
                      options={field.options}
                    />
                  );
                }
                if (field?.type === 'file') {
                  return (
                    <FileField
                      key={field?.key}
                      id={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      equipmentId={params.id}
                      field={field}
                    />
                  );
                }
                if (field?.type === 'files') {
                  return (
                    <FileField
                      key={field?.key}
                      id={field?.key}
                      label={field?.label}
                      setFields={setFields}
                      equipmentId={params.id}
                      field={field}
                      files={true}
                      fields={fields}
                    />
                  );
                }
              })}
          <div className='equipment__container'>
            <button className='equipment__button' onClick={submitHandler}>
              {location ? t('toolsCreate.buttons.save') : t('toolsCreate.buttons.create')}
            </button>
          </div>
        </div>
        {/*{*/}
        {/*  hasHose &&*/}
        {/*  <div className="equipmentHose">*/}
        {/*    <div className="equipmentHose__list">*/}
        {/*      {renderCreatedHoses}*/}
        {/*      {*/}
        {/*        hoseFields?.map((hose, index) => (*/}
        {/*          <HoseElement*/}
        {/*            key={index}*/}
        {/*            hose={hose}*/}
        {/*            index={index}*/}
        {/*            setHoseFields={setHoseFields}*/}
        {/*            getEquipment={() => getEquipment(params.id)}*/}
        {/*          />*/}
        {/*        ))*/}
        {/*      }*/}
        {/*    </div>*/}
        {/*    <div className="equipmentHose__btn" onClick={addNewHose}>*/}
        {/*      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">*/}
        {/*        <path fillRule="evenodd" clipRule="evenodd"*/}
        {/*              d="M16.3332 10.0003C16.3332 13.4981 13.4976 16.3337 9.99984 16.3337C6.50203 16.3337 3.6665 13.4981 3.6665 10.0003C3.6665 6.50252 6.50203 3.66699 9.99984 3.66699C13.4976 3.66699 16.3332 6.50252 16.3332 10.0003ZM18.3332 10.0003C18.3332 14.6027 14.6022 18.3337 9.99984 18.3337C5.39746 18.3337 1.6665 14.6027 1.6665 10.0003C1.6665 5.39795 5.39746 1.66699 9.99984 1.66699C14.6022 1.66699 18.3332 5.39795 18.3332 10.0003ZM9.99984 4.83366C10.5521 4.83366 10.9998 5.28137 10.9998 5.83366V9.00033H14.1665C14.7188 9.00033 15.1665 9.44804 15.1665 10.0003C15.1665 10.5526 14.7188 11.0003 14.1665 11.0003H10.9998V14.167C10.9998 14.7193 10.5521 15.167 9.99984 15.167C9.44755 15.167 8.99984 14.7193 8.99984 14.167V11.0003H5.83317C5.28089 11.0003 4.83317 10.5526 4.83317 10.0003C4.83317 9.44804 5.28089 9.00033 5.83317 9.00033H8.99984V5.83366C8.99984 5.28137 9.44755 4.83366 9.99984 4.83366Z"*/}
        {/*              fill="#27323C"/>*/}
        {/*      </svg>*/}
        {/*      <p>Add a hose</p>*/}
        {/*    </div>*/}
        {/*    /!*{*!/*/}
        {/*    /!*  hoseFields?.length > 0 &&*!/*/}
        {/*    /!*  <div className="equipmentHose__btn submit" onClick={createHose}>*!/*/}
        {/*    /!*    <p>Save hose changes</p>*!/*/}
        {/*    /!*  </div>*!/*/}
        {/*    /!*}*!/*/}

        {/*  </div>*/}
        {/*}*/}
      </div>
    </Wrapper>
  );
};

export default ToolsCreate;
